import React, { ChangeEvent, FC } from "react";
import FeatherIcon from "feather-icons-react";
import { EntityType, UploadedFileFeature } from "@green0meter/api-client";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import { Box, Button, IconButton, Stack } from "@components/base";
import {
  useDeleteCompanyLogo,
  useGetCurrentCompanyInfo,
  useUpdateCompanyLogo,
  useUploadFile,
} from "@api";
import { DownloadPreview } from "@components/common";
import { useUserStore } from "@store/user";

const CustomLogo: FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUserStore();
  const { currentCompanyInfo, currentCompanyInfoEnabled } =
    useGetCurrentCompanyInfo(user);
  const { updateCompanyLogo, updateCompanyLogoEnabled, isPendingUpdateCompanyLogo } =
    useUpdateCompanyLogo(user);
  const { deleteCompanyLogo, deleteCompanyLogoEnabled, isPendingDeleteCompanyLogo } =
    useDeleteCompanyLogo(user);
  const { uploadFileAsync, uploadFileEnabled } = useUploadFile(user);

  const handleFileChange = async (
    event: ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const file = event.target.files?.[0];

    if (!currentCompanyInfo || !file || !file.type.startsWith("image/")) {
      enqueueSnackbar(t("organization.customLogo.invalidFile"), {
        variant: "error",
      });

      return;
    }

    const uploadedFile = await uploadFileAsync({
      file,
      entityId: currentCompanyInfo.id,
      databaseEntityType: EntityType.Company,
      uploadedFileFeature: UploadedFileFeature.CompanyLogo,
    });

    if (uploadedFile) {
      updateCompanyLogo({
        companyId: currentCompanyInfo.id,
        fileId: uploadedFile.fileId,
      });
    }
  };

  const handleRemoveLogo = (): void => {
    if (currentCompanyInfo) {
      deleteCompanyLogo({
        companyId: currentCompanyInfo.id,
      });
    }
  };

  return currentCompanyInfoEnabled && currentCompanyInfo ? (
    <Box>
      {currentCompanyInfo.appearance?.logo ? (
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box maxWidth={200} maxHeight={200}>
            <DownloadPreview
              path={currentCompanyInfo.appearance.logo.path}
              name={currentCompanyInfo.appearance.logo.name}
            />
          </Box>
          {deleteCompanyLogoEnabled && (
            <IconButton
              onClick={handleRemoveLogo}
              color="error"
              disabled={isPendingDeleteCompanyLogo}
            >
              <FeatherIcon icon="trash-2" />
            </IconButton>
          )}
        </Stack>
      ) : (
        <Box>
          {updateCompanyLogoEnabled && uploadFileEnabled && (
            <Button
              component="label"
              variant="outlined"
              startIcon={<FeatherIcon icon="upload" />}
              isLoading={isPendingUpdateCompanyLogo}
            >
              {t("organization.customLogo.uploadCompanyLogo")}
              <input
                type="file"
                accept="image/*"
                multiple={false}
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
            </Button>
          )}
        </Box>
      )}
    </Box>
  ) : null;
};

export default CustomLogo;
